/**
 * @fileoverview UC-REG-253 / UC-REG-262 — Helpers de presentación del turno
 *   asociado a un trámite (Appointment sin slot, con `appointment.procedure`).
 *   Viven acá y no en los componentes porque `ProceduresList.vue`,
 *   `MyProcedures.vue`, `Turnos.vue` y `Home.vue` no se montan bajo vue-jest
 *   (optional chaining en el template) → así la lógica queda cubierta por
 *   tests unitarios.
 *
 *   UC-REG-262 (solo Campana, flag `isProcedureAppointmentByInstitution`):
 *   el staff elige la INSTITUCIÓN del turno; el Appointment se asigna a un
 *   prestador ficticio (usuario `procedureAppointmentDoctorUsername`) que
 *   nunca se muestra: en su lugar se ve la institución. Con el flag apagado
 *   (resto de tenants) todas las funciones se comportan como UC-REG-253.
 */
import {
  isProcedureAppointmentByInstitution,
  procedureAppointmentDoctorUsername,
} from "@/services/config";

/** @description Texto cuando un turno de trámite no tiene institución populada. */
export const NO_INSTITUTION_LABEL = "Institución a confirmar";

/**
 * @description Nombre "Nombre Apellido" de un User populado.
 * @param {Object} [user] - User con firstName/lastName
 * @returns {string} '' si no hay user
 */
export function fullName(user) {
  if (!user) return "";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim();
}

/**
 * @description Texto del turno de un trámite. UC-REG-253: "dd/MM/yyyy HH:mm
 *   · Dr. X · Sede". UC-REG-262 (por institución): "dd/MM/yyyy HH:mm ·
 *   Institución" — el prestador ficticio no se muestra.
 * @param {Object} a - Appointment populado (doctor/institution)
 * @param {function(Date|string): string} formatDate - Formateador de fecha
 *   del componente
 * @param {boolean} [byInstitution] - Modo institución (default: flag del build)
 * @returns {string} '' si no hay turno o no tiene fecha
 */
export function formatProcedureAppointment(
  a,
  formatDate,
  byInstitution = isProcedureAppointmentByInstitution
) {
  if (!a || !a.startsAt) return "";
  const parts = [formatDate(a.startsAt)];
  if (a.doctor && !byInstitution) parts.push(fullName(a.doctor));
  if (a.institution && a.institution.name) parts.push(a.institution.name);
  return parts.join(" · ");
}

/**
 * @description UC-REG-262 — El turno es de trámite y el tenant los asigna por
 *   institución → en vez del médico (ficticio) se muestra la institución.
 *   Siempre false con el flag apagado.
 * @param {Object} apt - Appointment (con `procedure` si es turno de trámite)
 * @param {boolean} [byInstitution] - Modo institución (default: flag del build)
 * @returns {boolean}
 */
export function hidesAppointmentDoctor(
  apt,
  byInstitution = isProcedureAppointmentByInstitution
) {
  return Boolean(byInstitution && apt && apt.procedure);
}

/**
 * @description Nombre de la institución del turno (populada) o un texto
 *   neutro si no vino.
 * @param {Object} apt - Appointment con `institution` populada
 * @returns {string}
 */
export function appointmentInstitutionName(apt) {
  const inst = apt && apt.institution;
  return (
    (inst && typeof inst === "object" && inst.name) || NO_INSTITUTION_LABEL
  );
}

/**
 * @description Saca los turnos de trámite que no muestran médico (UC-REG-262)
 *   de una lista — p. ej. para "Mis médicos" del paciente, que no debe
 *   ofrecer al prestador ficticio. Con el flag apagado devuelve la lista
 *   intacta.
 * @param {Array<Object>} appointments
 * @param {boolean} [byInstitution] - Modo institución (default: flag del build)
 * @returns {Array<Object>}
 */
export function withoutHiddenDoctorAppointments(
  appointments,
  byInstitution = isProcedureAppointmentByInstitution
) {
  if (!byInstitution) return appointments;
  return (appointments || []).filter(
    (apt) => !hidesAppointmentDoctor(apt, true)
  );
}

/**
 * @description UC-REG-262 — Busca el prestador ficticio de los turnos de
 *   trámite en la lista de `GET /doctors` por su username (sin distinguir
 *   mayúsculas).
 * @param {Array<Object>} doctors - Users médico (con `username`)
 * @param {string} [username] - Default: `VUE_APP_PROCEDURE_APPOINTMENT_DOCTOR_USERNAME`
 * @returns {Object|null}
 */
export function findProcedureAppointmentDoctor(
  doctors,
  username = procedureAppointmentDoctorUsername
) {
  const wanted = String(username || "")
    .trim()
    .toLowerCase();
  if (!wanted) return null;
  return (
    (doctors || []).find(
      (d) => d && String(d.username || "").toLowerCase() === wanted
    ) || null
  );
}

/**
 * @description Instituciones (populadas) de un médico de `GET /doctors`.
 * @param {Object} [doctor] - User médico con `doctor.institutions`
 * @returns {Array<{_id:string,name:string}>}
 */
export function doctorInstitutions(doctor) {
  const list = (doctor && doctor.doctor && doctor.doctor.institutions) || [];
  return list.filter((i) => i && i._id);
}

/**
 * @description UC-REG-262 — Valida la institución elegida en el form del
 *   turno y resuelve el médico (ficticio) + institución que viajan a la API
 *   (`appointmentDoctor` / `appointmentInstitution`, contrato UC-REG-253 sin
 *   cambios).
 * @param {Object} params
 * @param {Array<Object>} params.doctors - Lista de `GET /doctors`
 * @param {string} params.institution - Institution._id elegida
 * @param {string} [params.username] - Username del prestador ficticio
 * @returns {{doctor?: string, institution?: string, error?: string}}
 */
export function resolveInstitutionAppointment({
  doctors,
  institution,
  username = procedureAppointmentDoctorUsername,
}) {
  const doctor = findProcedureAppointmentDoctor(doctors, username);
  if (!doctor) {
    return {
      error:
        "Falta configurar el prestador de turnos de trámite. Avisale al administrador.",
    };
  }
  if (!institution) return { error: "Elegí la institución del turno" };
  const valid = doctorInstitutions(doctor).some((i) => i._id === institution);
  if (!valid) return { error: "La institución elegida no es válida" };
  return { doctor: doctor._id, institution };
}
